import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, CheckCircle2, AlertTriangle, Calendar, Activity } from 'lucide-react';
import { usePatternDetail, useAcknowledgePattern, useResolvePattern } from '../../hooks/useIntelligence';
import { AiRecommendationPanel } from '../../components/intelligence';
import { Card } from '../../components/ui/Card';
import { Loading } from '../../components/ui/Loading';
import toast from 'react-hot-toast';

export default function PatternDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const patternId = parseInt(id || '0');
    const { data, isLoading, error } = usePatternDetail(patternId);
    const acknowledgeMutation = useAcknowledgePattern();
    const resolveMutation = useResolvePattern();

    const handleAcknowledge = () => {
        acknowledgeMutation.mutate(patternId, {
            onSuccess: () => {
                toast.success('Pattern acknowledged');
            },
            onError: () => {
                toast.error('Failed to acknowledge pattern');
            }
        });
    };

    const handleResolve = () => {
        const note = prompt('Add a resolution note (optional):');
        resolveMutation.mutate({ patternId, note: note || undefined }, {
            onSuccess: () => {
                toast.success('Pattern marked as resolved');
                navigate('/intelligence/patterns');
            },
            onError: () => {
                toast.error('Failed to resolve pattern');
            }
        });
    };

    if (isLoading) return <Loading message="Loading pattern details..." />;

    if (error || !data) {
        return (
            <div className="text-center py-12">
                <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
                <h2 className="text-xl font-semibold text-gray-900">Pattern Not Found</h2>
                <Link to="/intelligence/patterns" className="text-primary-600 hover:underline mt-2 inline-block">
                    ← Back to Patterns
                </Link>
            </div>
        );
    }

    const { pattern, ai_insight } = data;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to="/intelligence/patterns"
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{pattern.severity_icon}</span>
                            <h1 className="text-2xl font-bold text-gray-900">{pattern.title}</h1>
                        </div>
                        <p className="text-gray-600">{pattern.description}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {pattern.status.is_resolved ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            <CheckCircle2 size={14} />
                            Resolved
                        </span>
                    ) : pattern.status.is_acknowledged ? (
                        <button
                            onClick={handleResolve}
                            disabled={resolveMutation.isPending}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                            Mark as Resolved
                        </button>
                    ) : (
                        <button
                            onClick={handleAcknowledge}
                            disabled={acknowledgeMutation.isPending}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            Acknowledge
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <TrendingUp className="text-red-600" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-600">Loss Rate</p>
                            <p className="text-xl font-bold text-gray-900">
                                {pattern.stats.loss_rate.toFixed(1)}%
                            </p>
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <AlertTriangle className="text-orange-600" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-600">Lift vs Baseline</p>
                            <p className="text-xl font-bold text-gray-900">
                                {pattern.stats.lift.toFixed(1)}x
                            </p>
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Activity className="text-blue-600" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-600">Affected Payments</p>
                            <p className="text-xl font-bold text-gray-900">
                                {pattern.stats.affected_payments}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Calendar className="text-purple-600" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-600">Detected</p>
                            <p className="text-xl font-bold text-gray-900">
                                {pattern.detection_count}x
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Impact Analysis */}
            <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 Financial Impact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <p className="text-sm text-gray-600">Daily Loss</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                            ${pattern.impact.estimated_daily_loss.toLocaleString()}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Weekly Loss</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                            ${pattern.impact.estimated_weekly_loss.toLocaleString()}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Monthly Loss</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                            ${pattern.impact.estimated_monthly_loss.toLocaleString()}
                        </p>
                    </div>
                </div>
                <p className="mt-4 text-sm text-gray-600">{pattern.impact.summary}</p>
            </Card>

            {/* Pattern Details */}
            <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Pattern Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-600">Type</p>
                        <p className="text-base font-medium text-gray-900 mt-1 capitalize">
                            {pattern.type.replace('_', ' ')}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Confidence</p>
                        <p className="text-base font-medium text-gray-900 mt-1 capitalize">
                            {pattern.confidence}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">First Detected</p>
                        <p className="text-base font-medium text-gray-900 mt-1">
                            {new Date(pattern.first_detected_at).toLocaleString()}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Last Detected</p>
                        <p className="text-base font-medium text-gray-900 mt-1">
                            {new Date(pattern.last_detected_at).toLocaleString()}
                        </p>
                    </div>
                </div>

                {Object.keys(pattern.conditions).length > 0 && (
                    <div className="mt-4">
                        <p className="text-sm text-gray-600 mb-2">Conditions</p>
                        <div className="bg-gray-50 rounded p-3">
                            <pre className="text-xs text-gray-700 font-mono">
                                {JSON.stringify(pattern.conditions, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}
            </Card>

            {/* AI Insights */}
            {ai_insight && (
                <AiRecommendationPanel
                    recommendations={ai_insight.recommendations}
                    rootCause={ai_insight.hypothesis}
                    provider="AI Analysis"
                    cached={ai_insight.cached}
                />
            )}

            {/* Sample Payments */}
            {pattern.sample_payments && pattern.sample_payments.length > 0 && (
                <Card>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Sample Affected Payments</h3>
                    <div className="flex flex-wrap gap-2">
                        {pattern.sample_payments.map((paymentId) => (
                            <Link
                                key={paymentId}
                                to={`/payments/${paymentId}`}
                                className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm font-mono"
                            >
                                #{paymentId}
                            </Link>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}
